// Shared grid calculation utilities for addRowColumn and gridLineRecalculation

/**
 * Calculate word center X coordinate from various bounding box formats
 * Handles both normalized (0-1) and absolute coordinate systems
 */
export const calculateWordCenterX = (word, pdfPageDimensions) => {
  const bbox = word.bbox;
  if (!bbox) return 0;

  const { width: pdfWidth } = pdfPageDimensions.current;

  // Handle left/top/width/height format
  if (
    bbox.left !== undefined &&
    bbox.top !== undefined &&
    bbox.width !== undefined &&
    bbox.height !== undefined
  ) {
    if (bbox.left <= 1 && bbox.width <= 1) {
      // Normalized coordinates
      return (bbox.left + bbox.width / 2) * pdfWidth;
    } else {
      // Absolute coordinates
      return bbox.left + bbox.width / 2;
    }
  }

  // Handle x0/y0/x1/y1 format
  const x0 = bbox.x0 || bbox.x || 0;
  const x1 = bbox.x1 || bbox.right || 0;

  if (x0 <= 1 && x1 <= 1) {
    // Normalized coordinates
    return ((x0 + x1) / 2) * pdfWidth;
  } else {
    // Absolute coordinates
    return (x0 + x1) / 2;
  }
};

/**
 * Calculate word center Y coordinate from various bounding box formats
 * Handles both normalized (0-1) and absolute coordinate systems
 */
export const calculateWordCenterY = (word, pdfPageDimensions) => {
  const bbox = word.bbox;
  if (!bbox) return 0;

  const { height: pdfHeight } = pdfPageDimensions.current;

  // Handle left/top/width/height format
  if (
    bbox.left !== undefined &&
    bbox.top !== undefined &&
    bbox.width !== undefined &&
    bbox.height !== undefined
  ) {
    if (bbox.top <= 1 && bbox.height <= 1) {
      // Normalized coordinates
      return (bbox.top + bbox.height / 2) * pdfHeight;
    } else {
      // Absolute coordinates
      return bbox.top + bbox.height / 2;
    }
  }

  // Handle x0/y0/x1/y1 format
  const y0 = bbox.y0 || bbox.y || 0;
  const y1 = bbox.y1 || bbox.bottom || 0;

  if (y0 <= 1 && y1 <= 1) {
    // Normalized coordinates
    return ((y0 + y1) / 2) * pdfHeight;
  } else {
    // Absolute coordinates
    return (y0 + y1) / 2;
  }
};

/**
 * Calculate both X and Y word center coordinates
 * Returns an object with x and y properties
 */
export const calculateWordCenter = (word, pdfPageDimensions) => {
  return {
    x: calculateWordCenterX(word, pdfPageDimensions),
    y: calculateWordCenterY(word, pdfPageDimensions),
  };
};

/**
 * Process bounding box to get normalized coordinates
 * Converts various bbox formats to a consistent x0, y0, x1, y1 format
 */
export const processBoundingBox = (bbox, pdfPageDimensions) => {
  if (!bbox) return null;

  const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;
  let x0, y0, x1, y1;

  // Handle left/top/width/height format
  if (
    bbox.left !== undefined &&
    bbox.top !== undefined &&
    bbox.width !== undefined &&
    bbox.height !== undefined
  ) {
    if (
      bbox.left <= 1 &&
      bbox.top <= 1 &&
      bbox.width <= 1 &&
      bbox.height <= 1
    ) {
      // Normalized coordinates - convert to absolute
      x0 = bbox.left * pdfWidth;
      y0 = bbox.top * pdfHeight;
      x1 = (bbox.left + bbox.width) * pdfWidth;
      y1 = (bbox.top + bbox.height) * pdfHeight;
    } else {
      // Already absolute coordinates
      x0 = bbox.left;
      y0 = bbox.top;
      x1 = bbox.left + bbox.width;
      y1 = bbox.top + bbox.height;
    }
  } else {
    // Handle x0/y0/x1/y1 format
    x0 = bbox.x0 || bbox.x || 0;
    y0 = bbox.y0 || bbox.y || 0;
    x1 = bbox.x1 || bbox.right || 0;
    y1 = bbox.y1 || bbox.bottom || 0;

    if (x0 <= 1 && y0 <= 1 && x1 <= 1 && y1 <= 1) {
      // Normalized coordinates - convert to absolute
      x0 *= pdfWidth;
      y0 *= pdfHeight;
      x1 *= pdfWidth;
      y1 *= pdfHeight;
    }
  }

  return { x0, y0, x1, y1 };
};

/**
 * Calculate cell bounds from words
 * Returns the minimum and maximum coordinates of all words in the cell
 */
export const calculateCellBounds = (words, pdfPageDimensions) => {
  if (!words || words.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  words.forEach((word) => {
    const bounds = processBoundingBox(word.bbox, pdfPageDimensions);
    if (bounds) {
      minX = Math.min(minX, bounds.x0);
      minY = Math.min(minY, bounds.y0);
      maxX = Math.max(maxX, bounds.x1);
      maxY = Math.max(maxY, bounds.y1);
    }
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
  };
};

/**
 * Extract all cell bounds from selectedTable for a given page
 * Returns an array of cell bounds with row/column indices
 */
export const extractCellBoundsFromTable = (
  selectedTable,
  currentPage,
  pdfPageDimensions
) => {
  const cellBounds = [];

  Object.keys(selectedTable).forEach((cellKey) => {
    if (cellKey.match(/^\d+_\d+$/)) {
      const cellLocationData = selectedTable[cellKey];
      if (cellLocationData && Array.isArray(cellLocationData)) {
        const firstCellInfo = cellLocationData[0];
        if (
          firstCellInfo &&
          firstCellInfo.page === currentPage &&
          firstCellInfo.words
        ) {
          const [rowStr, colStr] = cellKey.split("_");
          const rowIndex = parseInt(rowStr);
          const colIndex = parseInt(colStr);

          const bounds = calculateCellBounds(
            firstCellInfo.words,
            pdfPageDimensions
          );
          if (bounds) {
            cellBounds.push({
              rowIndex,
              colIndex,
              ...bounds,
            });
          }
        }
      }
    }
  });

  return cellBounds;
};

/**
 * Generate grid lines from cell bounds
 * Creates horizontal and vertical grid lines based on cell positions
 */
export const generateGridLinesFromCellBounds = (cellBounds, padding = 5) => {
  const gridLines = {
    horizontal: [],
    vertical: [],
  };

  if (cellBounds.length === 0) {
    return gridLines;
  }

  // 1. Calculate global boundaries to ensure a uniform, rectangular grid
  const tableMinX = Math.min(...cellBounds.map((c) => c.minX)) - padding;
  const tableMaxX = Math.max(...cellBounds.map((c) => c.maxX)) + padding;
  const tableMinY = Math.min(...cellBounds.map((c) => c.minY)) - padding;
  const tableMaxY = Math.max(...cellBounds.map((c) => c.maxY)) + padding;

  // 2. Calculate the maximum bounds to avoid generating outermost lines
  const globalMaxY = Math.max(...cellBounds.map((c) => c.maxY));
  const globalMaxX = Math.max(...cellBounds.map((c) => c.maxX));

  // 3. Generate horizontal lines, extending them to the full table width
  const rowGroups = {};
  cellBounds.forEach((cell) => {
    if (!rowGroups[cell.rowIndex]) rowGroups[cell.rowIndex] = [];
    rowGroups[cell.rowIndex].push(cell);
  });

  Object.keys(rowGroups).forEach((rowKey) => {
    const rowCells = rowGroups[rowKey];
    if (rowCells.length > 0) {
      const maxY = Math.max(...rowCells.map((c) => c.maxY));
      // Skip the bottommost line to avoid outer box
      if (maxY < globalMaxY) {
        gridLines.horizontal.push({
          x0: tableMinX, // Use global minX
          y0: maxY + 2,
          x1: tableMaxX, // Use global maxX
          y1: maxY + 2,
        });
      }
    }
  });

  // 4. Generate vertical lines, extending them to the full table height
  const colGroups = {};
  cellBounds.forEach((cell) => {
    if (!colGroups[cell.colIndex]) colGroups[cell.colIndex] = [];
    colGroups[cell.colIndex].push(cell);
  });

  Object.keys(colGroups).forEach((colKey) => {
    const colCells = colGroups[colKey];
    if (colCells.length > 0) {
      const maxX = Math.max(...colCells.map((c) => c.maxX));
      // Skip the rightmost line to avoid outer box
      if (maxX < globalMaxX) {
        gridLines.vertical.push({
          x0: maxX + 2,
          y0: tableMinY, // Use global minY
          x1: maxX + 2,
          y1: tableMaxY, // Use global maxY
        });
      }
    }
  });

  return gridLines;
};

/**
 * Sort words by position for natural reading order
 * First by Y position (top to bottom), then by X position (left to right)
 */
export const sortWordsByPosition = (words, tolerance = 5) => {
  return words.sort((a, b) => {
    const aY = a.bbox?.top || a.bbox?.y0 || 0;
    const bY = b.bbox?.top || b.bbox?.y0 || 0;
    const aX = a.bbox?.left || a.bbox?.x0 || 0;
    const bX = b.bbox?.left || b.bbox?.x0 || 0;

    // First sort by Y position (top to bottom)
    if (Math.abs(aY - bY) > tolerance) {
      return aY - bY;
    }
    // Then by X position (left to right)
    return aX - bX;
  });
};

/**
 * Determine cell position based on grid lines
 * Returns row and column indices for a given word position
 */
export const determineCellPosition = (
  wordCenter,
  horizontalLines,
  verticalLines
) => {
  let rowIndex = 0;
  let colIndex = 0;

  // Find row (between horizontal lines)
  const sortedHorizontal = [...horizontalLines].sort((a, b) => a.y0 - b.y0);
  for (let i = 0; i < sortedHorizontal.length; i++) {
    if (wordCenter.y > sortedHorizontal[i].y0) {
      rowIndex = i + 1;
    }
  }

  // Find column (between vertical lines)
  const sortedVertical = [...verticalLines].sort((a, b) => a.x0 - b.x0);
  for (let i = 0; i < sortedVertical.length; i++) {
    if (wordCenter.x > sortedVertical[i].x0) {
      colIndex = i + 1;
    }
  }

  return { rowIndex, colIndex };
};

/**
 * Check if a word is near a grid line within threshold
 */
export const isWordNearGridLine = (wordCenter, gridLines, threshold = 50) => {
  // Check vertical lines
  for (const line of gridLines.vertical || []) {
    if (Math.abs(wordCenter.x - line.x0) < threshold) {
      return true;
    }
  }

  // Check horizontal lines
  for (const line of gridLines.horizontal || []) {
    if (Math.abs(wordCenter.y - line.y0) < threshold) {
      return true;
    }
  }

  return false;
};
